import { useState, useEffect, useRef } from "react";
import api from "@/api/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileImage, FileText, FileVideo2, ImagePlus, Trash2, UploadCloud, Video } from "lucide-react";

interface ContentItem {
  id: string;
  type: "text" | "image" | "video";
  imgUrl?: string;
  videoUrl?: string;
  text?: string;
  format?: "jpg" | "gif";
  html_enabled?: boolean;
}

interface ImageContent extends ContentItem {
  type: "image";
  imgUrl: string;
  text?: string;
  format: "jpg" | "gif";
}

interface VideoContent extends ContentItem {
  type: "video";
  videoUrl: string;
  text?: string;
}

export default function ContentSection({ contents, refresh, preview = false }: { contents: ContentItem[]; refresh: () => void; preview?: boolean }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [contentType, setContentType] = useState<"text" | "image" | "video">("text");
  const [enableHtml, setEnableHtml] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [previewNotice, setPreviewNotice] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await api.post("/api/upload_content", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contents"] });
      setText("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/api/delete_content", { id });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contents"] });
      refresh();
    },
  });

  useEffect(() => {
    if (!deleteConfirmId) return;
    cancelDeleteRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !deleteMutation.isPending) setDeleteConfirmId(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [deleteConfirmId, deleteMutation.isPending]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    const loadMedia = async () => {
      const imageContents = contents.filter((content): content is ImageContent => 
        content.type === "image" && !!content.imgUrl && !(preview && content.imgUrl.startsWith("/images/"))
      );
      const videoContents = contents.filter((content): content is VideoContent => 
        content.type === "video" && !!content.videoUrl
      );

      const newImageUrls: Record<string, string> = {};
      const newVideoUrls: Record<string, string> = {};
      
      for (const content of imageContents) {
        try {
          const response = await api.get(content.imgUrl, {
            responseType: 'blob'
          });
          const blobUrl = URL.createObjectURL(response.data);
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            continue;
          }
          objectUrls.push(blobUrl);
          newImageUrls[content.id] = blobUrl;
        } catch (error) {
          console.error("图片加载失败", error);
        }
      }

      for (const content of videoContents) {
        try {
          const response = await api.get(content.videoUrl, {
            responseType: 'blob'
          });
          const blobUrl = URL.createObjectURL(response.data);
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            continue;
          }
          objectUrls.push(blobUrl);
          newVideoUrls[content.id] = blobUrl;
        } catch (error) {
          console.error("视频加载失败", error);
        }
      }

      if (!cancelled) {
        setImageUrls(newImageUrls);
        setVideoUrls(newVideoUrls);
      }
    };

    loadMedia();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [contents, preview]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (preview) {
      setPreviewNotice(true);
      return;
    }
    if (contentType === "text" && !text.trim()) {
      alert("请输入文字内容");
      return;
    }
    if ((contentType === "image" || contentType === "video") && !file) {
      alert("请选择文件");
      return;
    }

    const formData = new FormData();
    formData.append("type", contentType);
    if (contentType === "text") {
      formData.append("text", text);
      formData.append("html_enabled", enableHtml.toString());
    } else if (file) {
      formData.append("file", file);
      formData.append("text", text);
      formData.append("html_enabled", enableHtml.toString());
    }

    uploadMutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (contentType === "image" && !selectedFile.type.startsWith("image/")) {
        alert("请选择图片文件");
        return;
      }
      if (contentType === "video" && !selectedFile.type.startsWith("video/")) {
        alert("请选择视频文件");
        return;
      }
      setFile(selectedFile);
    }
  };

  const confirmDelete = (id: string) => {
    if (preview) {
      setDeleteConfirmId(null);
      setPreviewNotice(true);
      return;
    }
    deleteMutation.mutate(id, { onSuccess: () => setDeleteConfirmId(null) });
  };

  return (
    <div className="user-content-section">
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="user-card user-content-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-content-title" aria-describedby="delete-content-description">
            <h3 id="delete-content-title">确认删除</h3>
            <p id="delete-content-description">确定要删除这条内容吗？此操作不可恢复。</p>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                ref={cancelDeleteRef}
                onClick={() => setDeleteConfirmId(null)}
                className="user-button user-button-secondary"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => confirmDelete(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="user-button user-button-danger"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleUpload} className="user-content-form">
        <div className="user-config-toolbar"><div><h3>打卡内容</h3><p>选择发送内容的类型并添加到列表</p></div></div>
        <div className="user-content-modes" role="group" aria-label="内容类型">
          <button
            type="button"
          onClick={() => { setContentType("text"); setFile(null); }}
            aria-pressed={contentType === "text"}
            className={`user-content-mode ${contentType === "text" ? "is-active" : ""}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 7V4h16v3"></path>
              <path d="M9 20h6"></path>
              <path d="M12 4v16"></path>
            </svg>
            <span>文字</span>
          </button>
          <button
            type="button"
            onClick={() => { setContentType("image"); setFile(null); }}
            aria-pressed={contentType === "image"}
            className={`user-content-mode ${contentType === "image" ? "is-active" : ""}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <span>图片</span>
          </button>
          <button
            type="button"
            onClick={() => { setContentType("video"); setFile(null); }}
            aria-pressed={contentType === "video"}
            className={`user-content-mode ${contentType === "video" ? "is-active" : ""}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            <span>视频</span>
          </button>
        </div>

        {contentType === "text" ? (
          <div className="user-content-fields">
            <label htmlFor="checkin-content-text">打卡文字</label>
            <textarea
              id="checkin-content-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="输入打卡文字内容"
              className="user-content-textarea"
            />
            <div className="user-content-checkbox">
              <input
                type="checkbox"
                id="enableHtml"
                checked={enableHtml}
                onChange={(e) => setEnableHtml(e.target.checked)}
                className="w-4 h-4 text-brand bg-gray-700 border-gray-600 rounded focus:ring-brand"
              />
              <label htmlFor="enableHtml" className="text-sm text-gray-300">
                启用 HTML 渲染（支持加粗、斜体、链接等格式）
              </label>
            </div>
          </div>
        ) : (
          <div className="user-content-fields">
            <div className="user-upload-field">
              <input
                id="checkin-content-file"
                ref={fileInputRef}
                type="file"
                key={contentType}
                accept={contentType === "image" ? "image/*" : "video/*"}
                onChange={handleFileChange}
                className="user-content-file"
              />
              <label htmlFor="checkin-content-file" className={`user-upload-picker ${file ? "has-file" : ""}`}>
                <span className="user-upload-icon">{file ? (contentType === "image" ? <FileImage size={25} /> : <FileVideo2 size={25} />) : <UploadCloud size={27} />}</span>
                <span className="user-upload-copy"><strong>{file ? file.name : contentType === "image" ? "选择要发送的图片" : "选择要发送的视频"}</strong><small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · 点击更换文件` : contentType === "image" ? "JPG 格式，最大 10 MB" : "MP4、MOV、M4V、3GP 格式，最大 50 MB"}</small></span>
                <span className="user-upload-action">浏览文件</span>
              </label>
            </div>
            <label htmlFor="checkin-content-caption">文字说明（选填）</label>
            <textarea
              id="checkin-content-caption"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="添加文字说明（可选）"
              className="user-content-textarea"
            />
            <div className="user-content-checkbox">
              <input
                type="checkbox"
                id="enableHtml"
                checked={enableHtml}
                onChange={(e) => setEnableHtml(e.target.checked)}
                className="w-4 h-4 text-brand bg-gray-700 border-gray-600 rounded focus:ring-brand"
              />
              <label htmlFor="enableHtml" className="text-sm text-gray-300">
                启用 HTML 渲染（支持加粗、斜体、链接等格式）
              </label>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={uploadMutation.isPending}
          className="user-button user-button-primary user-content-submit"
        >
          {uploadMutation.isPending ? "上传中..." : "添加内容"}
        </button>
        {previewNotice && <p className="user-inline-notice" role="status">这是界面预览，内容不会保存或删除。</p>}
        {uploadMutation.isError && <p className="user-form-error" role="alert">添加失败，请检查文件后重试。</p>}
      </form>

      <div className="user-content-list">
        <h3>已添加的内容 <span>{contents.length}</span></h3>
        {deleteMutation.isError && <p className="user-form-error" role="alert">删除失败，请稍后重试。</p>}
        {contents.length === 0 && <p className="user-config-empty">还没有添加打卡内容</p>}
        {contents.map((content) => (
          <div
            key={content.id}
            className="user-content-item"
          >
            <div className="user-content-item-header">
              <span className={`user-content-kind is-${content.type}`}>
                {content.type === "text" ? <FileText size={15} /> : content.type === "image" ? <ImagePlus size={15} /> : <Video size={15} />}
                {content.type === "text" ? "文字" : content.type === "image" ? "图片" : "视频"}
              </span>
              <button type="button"
                onClick={() => setDeleteConfirmId(content.id)}
                className="user-content-delete"
                aria-label={`删除${content.type === "text" ? "文字" : content.type === "image" ? "图片" : "视频"}内容`}
              >
                <Trash2 size={15} /> 删除
              </button>
            </div>
            <div className="user-content-item-body">
              {content.type === "text" && (
                <div className="user-content-copy">
                  {content.html_enabled ? (
                    <div 
                      className="text-white break-words overflow-x-auto"
                      dangerouslySetInnerHTML={{ __html: (content.text || '').replace(/\n/g, '<br />') }}
                    />
                  ) : (
                    <p className="text-white whitespace-pre-wrap break-words">{content.text}</p>
                  )}
                  {content.html_enabled && (
                    <span className="text-xs text-gray-400">HTML 渲染已启用</span>
                  )}
                </div>
              )}
              {content.type === "image" && (
                <div className="user-content-media-content">
                  <div className="user-content-media-frame"><img
                    src={preview && content.imgUrl?.startsWith("/images/") ? content.imgUrl : imageUrls[content.id]}
                    alt="打卡图片"
                  /></div>
                  {content.text && (
                    <div className="user-content-copy">
                      {content.html_enabled ? (
                        <div 
                          className="text-white break-words overflow-x-auto"
                          dangerouslySetInnerHTML={{ __html: content.text.replace(/\n/g, '<br />') }}
                        />
                      ) : (
                        <p className="text-white whitespace-pre-wrap break-words">{content.text}</p>
                      )}
                      {content.html_enabled && (
                        <span className="text-xs text-gray-400">HTML 渲染已启用</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {content.type === "video" && (
                <div className="user-content-media-content">
                  <div className="user-content-media-frame"><video
                    src={videoUrls[content.id]}
                    controls
                  /></div>
                  {content.text && (
                    <div className="user-content-copy">
                      {content.html_enabled ? (
                        <div 
                          className="text-white break-words overflow-x-auto"
                          dangerouslySetInnerHTML={{ __html: content.text.replace(/\n/g, '<br />') }}
                        />
                      ) : (
                        <p className="text-white whitespace-pre-wrap break-words">{content.text}</p>
                      )}
                      {content.html_enabled && (
                        <span className="text-xs text-gray-400">HTML 渲染已启用</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
